import { Box, Card, CardContent, Button } from "@mui/material";
import { useState, ChangeEvent, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import { ApiResponse } from "../../../models/ApiResponse";
import api from "../../../services/ApiService";
import FileDownload from "../../../components/FileDownload";
import Loader from "../../../components/Loader";
import { LEAGO_TO_PIM_FIELD_LIST } from "../../../utils/constants";
import SessionService from "../../../services/SessionService";
import { Permissions } from "../../../models/Enum";

const LeagoToPIMFieldList = () => {
    const manageAppSetting = SessionService.getInstance().hasPermission(Permissions.ManageAppSettings);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileData, setFileData] = useState<any[]>([]);
    const [columnData, setColumnData] = useState<any[]>([]);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setSelectedFile(file);
    };

    const handleClear = () => {
        setColumnData([]);
        setFileData([]);
        setIsError(false);
        setSelectedFile(null);
        fileInputRef.current.value = "";
    };
    const fileInputRef = useRef(null);

    const handleBrowseClick = () => {
        setColumnData([]);
        setFileData([]);
        setIsError(false);
        fileInputRef.current.click();
    };

    const processErrorData = (errorResponse) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            const result = e.target?.result;

            if (!(result instanceof ArrayBuffer)) {
                console.error("Invalid file format");
                return;
            }

            const data = new Uint8Array(result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];

            let rowData: any = XLSX.utils.sheet_to_json(sheet, {
                defval: "",    // Fill empty cells with empty string
                raw: false,    // Parse formatted values (not raw cell formulas)
            });

            // Optional: remove last row if it's entirely empty
            if (
                rowData.length &&
                Object.values(rowData[rowData.length - 1]).every((val) => val === "")
            ) {
                rowData = rowData.slice(0, -1);
            }

            const errorRowData = rowData.filter(
                (_, index) => errorResponse?.errors?.includes((index + 1).toString()) // Assuming errors can be row numbers
            );

            if (errorRowData?.length > 0) {
                const columns = Object.keys(errorRowData[0]).map((field) => ({
                    field,
                    headerName: field,
                    width: field === "Include in PIM Export" ? 160 : 350,
                    disableColumnMenu: true,
                    sortable: false,
                }));

                const convertedData = errorRowData.map((item, index) => ({
                    ...item,
                    id: `${index + 1}`,
                }));

                setColumnData(columns);
                setFileData(convertedData);
            }

            fileInputRef.current.value = ""; // Reset file input
            setErrorMessage(errorResponse.message || "An error occurred");
            setSelectedFile(null);
            setIsError(true);
        };

        reader.readAsArrayBuffer(selectedFile);
    };

    const handleSubmit = async () => {
        const maxSizeInBytes = 5 * 1024 * 1024; // 5MB
        const formData = new FormData();
        if (selectedFile.size < maxSizeInBytes) {
            formData.append("file", selectedFile); // Assuming 'file' is the key expected by the server
            api
                .put<ApiResponse<any>>("/Picklist/leago-inriver-column-bulk-update", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((response) => {
                    if (response.isSuccess) {

                        if (response?.errors?.length > 0) {
                            toast.warning(response.message);
                            processErrorData(response);
                        }

                        toast.success("Leago to PIM field list updated successfully");
                        setSelectedFile(null);
                        fileInputRef.current.value = "";
                    }
                })
                .catch((error) => {
                    if (error?.response?.data?.errors?.length > 0) {
                        const errorResponse = error.response.data;
                        processErrorData(errorResponse);
                    }
                    console.error("Exception from bulk update Leago to PIM field list file", error);
                });
        } else {
            toast.error("Upload file less than 5MB");
        }
    };

    const handleExportPIMFields = async () => {
        let fileName = 'leago_To_PIM_Field_List';
        const fileDownload = await FileDownload({
            requestInput: null,
            inputFileName: fileName,
            inputFileFormat: LEAGO_TO_PIM_FIELD_LIST,
            setLoading
        });
        await fileDownload.handleDownload();
    };

    return (
        <Card className="leago-to-pim-field-list-card bulk-common-upload">
            <CardContent>
                {!loading ? <>
                    <div className="title">
                        <p>Bulk Update LEAGO to PIM Field List from Excel</p>
                    </div>
                    <Box className="container" >
                        <input
                            className="file-input"
                            type="file"
                            id="fileInput"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".xls,.xlsx"
                        />
                        <div className="inputWrap">
                            <input
                                type="text"
                                name="fileName"
                                id="fileName"
                                value={selectedFile ? selectedFile.name : ""}
                                readOnly
                            />

                            <p>
                                XLSX, XLS (max. 5MB).
                                <span className="download-sample"
                                    onClick={handleExportPIMFields}
                                >
                                    <strong>Export LEAGO to PIM Field List to Excel</strong>
                                </span>
                            </p>
                        </div>

                        <Button
                            onClick={handleBrowseClick}
                            variant="contained"
                            disabled={!manageAppSetting}
                        >
                            Browse
                        </Button>

                        <Button
                            onClick={handleSubmit}
                            disabled={(!selectedFile || !manageAppSetting) ? true : false}
                        >
                            Submit
                        </Button>
                        <Button className="clear-button"
                            onClick={handleClear}
                            disabled={!manageAppSetting}
                        >
                            Clear
                        </Button>
                    </Box>

                    {isError ? (
                        <>
                            <div className="errors-container pim-field-list-error">
                                <p>{errorMessage}</p>
                            </div>
                            <Box
                                className="bulk-update-grid-cell pim-field-errors-list-grid"
                            >
                                {fileData.length > 0 && (
                                    <DataGrid
                                        rows={fileData}
                                        columns={columnData}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: false,
                                            },
                                        }}
                                        className="pim-list-error-datagrid"
                                        disableColumnFilter
                                        disableColumnSelector
                                        disableDensitySelector
                                        disableRowSelectionOnClick={true}
                                        getRowHeight={() => "auto"}
                                    />
                                )}
                            </Box>
                        </>
                    ) : (
                        ""
                    )}
                </> : <Loader isLoading={loading} />}
            </CardContent>
        </Card>
    );
};

export default LeagoToPIMFieldList;
